/**
 * シャンチー (Xiangqi / 中国象棋) の TypeScript 表現。
 *
 * mixed-engine の serde 形 (XiangqiState / XiangqiMove / XiangqiOutcome) と完全に一致する。
 * 盤は 9 列 × 10 行、 駒は **交点上** に置かれる (board[rank][file] の 2 次元配列で表現)。
 * rank=0 が紅 (First) 最下段、 file=0 が左端。
 */

export type Side = 'First' | 'Second';

export type XiangqiPieceKind =
  | 'General'
  | 'Advisor'
  | 'Elephant'
  | 'Horse'
  | 'Chariot'
  | 'Cannon'
  | 'Pawn';

export interface XiangqiPiece {
  kind: XiangqiPieceKind;
  owner: Side;
}

export type XiangqiWinReason =
  | 'Checkmate'
  | 'Stalemate'
  | 'PerpetualCheck'
  | 'Resign'
  | 'Timeout';

export type XiangqiDrawReason =
  | 'SixtyMoveRule'
  | 'Threefold'
  | 'MutualPerpetualCheck'
  | 'DrawAgreement';

export type XiangqiOutcome =
  | { Win: { winner: Side; reason: XiangqiWinReason } }
  | { Draw: { reason: XiangqiDrawReason } };

export interface XiangqiState {
  board: (XiangqiPiece | null)[][];
  side: Side;
  halfmove_clock: number;
  fullmove_number: number;
  position_history: string[];
  move_was_check: boolean[];
  outcome: XiangqiOutcome | null;
}

export interface XiangqiMove {
  /** `(file, rank)`。 file=0..8、 rank=0..9。 紅最下段が rank=0。 */
  from: [number, number];
  to: [number, number];
}

export const XIANGQI_FILES = 9;
export const XIANGQI_RANKS = 10;

/** `legal` の中から from が一致する全 to 候補を抽出。 */
export function legalTargetsFor(legal: XiangqiMove[], from: [number, number]): XiangqiMove[] {
  return legal.filter((m) => m.from[0] === from[0] && m.from[1] === from[1]);
}

/** 駒の漢字を返す (紅=俥馬相仕帥炮兵、 黒=車馬象士將砲卒)。 */
export function pieceGlyph(piece: XiangqiPiece): string {
  if (piece.owner === 'First') {
    switch (piece.kind) {
      case 'General':
        return '帥';
      case 'Advisor':
        return '仕';
      case 'Elephant':
        return '相';
      case 'Horse':
        return '馬';
      case 'Chariot':
        return '俥';
      case 'Cannon':
        return '炮';
      case 'Pawn':
        return '兵';
    }
  } else {
    switch (piece.kind) {
      case 'General':
        return '將';
      case 'Advisor':
        return '士';
      case 'Elephant':
        return '象';
      case 'Horse':
        return '馬';
      case 'Chariot':
        return '車';
      case 'Cannon':
        return '砲';
      case 'Pawn':
        return '卒';
    }
  }
}
